import React, { Suspense, lazy } from 'react';
import { createBrowserRouter } from "react-router-dom";
import { Box, CircularProgress } from '@mui/material';
import { ProtectedRoute } from "./components/ProtectedRoute";
import { PublicRoute } from "./components/PublicRoute";

// Lazy load all page components for code splitting
const Home = lazy(() => import("./pages/home/home"));
const Signup = lazy(() => import("./pages/signup/signup"));
const ForgotPassword = lazy(() => import("./pages/forgot_password/forgot_password"));
const ResetPassword = lazy(() => import("./pages/reset_password/reset_password"));
const VerifyEmail = lazy(() => import("./pages/verify_email/verify_email"));
const AuthCallback = lazy(() => import("./pages/auth_sso/auth_callback"));
const Dashboard = lazy(() => import("./pages/dashboard/dashboard"));
const Clusters = lazy(() => import("./pages/clusters/clusters"));
const DsrcOverview = lazy(() => import("./pages/datasources/datasource"));
const DataFiles = lazy(() => import("./pages/data_files/data_files"));
const AllFiles = lazy(() => import("./pages/all_files/all_files"));
const ClusterDebugInfoPage = lazy(() => import("./pages/cluster_debug/cluster_debug_info_page"));
const Crashlog = lazy(() => import("./pages/crashlog/crashlog"));
const DbQueryRunner = lazy(() => import("./pages/db_query/db_query_runner"));
const Notifications = lazy(() => import("./pages/notifications/notifications"));
const PerformanceThresholds = lazy(() => import("./pages/notifications/thresholds"));
const PerformanceAlerts = lazy(() => import("./pages/performance_alerts/performance_alerts"));
const NotificationSettings = lazy(() => import("./pages/notification_settings/notification_settings"));
const NotFound = lazy(() => import("./pages/notfound/NotFound"));

// Loading fallback component
const PageLoader = () => (
    <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        width: '100%'
    }}>
        <CircularProgress size={40} />
    </Box>
);

// Wrap component with Suspense
const withSuspense = (Component) => (
    <Suspense fallback={<PageLoader />}>
        {Component}
    </Suspense>
);

const router = createBrowserRouter([
    {
        path: "/",
        element: <PublicRoute>{withSuspense(<Home />)}</PublicRoute>,
    },
    {
        path: "/signup",
        element: <PublicRoute>{withSuspense(<Signup />)}</PublicRoute>,
    },
    {
        path: "/forgot-password",
        element: <PublicRoute>{withSuspense(<ForgotPassword />)}</PublicRoute>
    },
    {
        path: "/reset_password/:token",
        element: <PublicRoute>{withSuspense(<ResetPassword />)}</PublicRoute>
    },
    {
        path: "/dashboard",
        element: <ProtectedRoute>{withSuspense(<Dashboard />)}</ProtectedRoute>,
    },
    {
        path: "/dashboard/:clusterName/datasources/overview",
        element: <ProtectedRoute>{withSuspense(<DsrcOverview />)}</ProtectedRoute>,
    },
    {
        path: "/clusters",
        element: <ProtectedRoute>{withSuspense(<Clusters />)}</ProtectedRoute>
    },
    {
        path: "/files/:clusterName/:tableName",
        element: <ProtectedRoute>{withSuspense(<DataFiles />)}</ProtectedRoute>
    },
    {
        path: "/allfiles/:clusterName",
        element: <ProtectedRoute>{withSuspense(<AllFiles />)}</ProtectedRoute>
    },
    {
        path: "/cluster_debug_info/:clusterName",
        element: <ProtectedRoute>{withSuspense(<ClusterDebugInfoPage />)}</ProtectedRoute>
    },
    {
        path: "/crashlog/:clusterName",
        element: <ProtectedRoute>{withSuspense(<Crashlog />)}</ProtectedRoute>
    },
    {
        path: "/query_database",
        element: <ProtectedRoute>{withSuspense(<DbQueryRunner />)}</ProtectedRoute>
    },
    {
        path: "/verify/:token",
        element: <PublicRoute>{withSuspense(<VerifyEmail />)}</PublicRoute>
    },
    {
        path: "/auth/callback",
        element: <PublicRoute>{withSuspense(<AuthCallback />)}</PublicRoute>
    },
    {
        path: "/notifications",
        element: <ProtectedRoute>{withSuspense(<Notifications />)}</ProtectedRoute>
    },
    {
        path: "/notifications/thresholds",
        element: <ProtectedRoute>{withSuspense(<PerformanceThresholds />)}</ProtectedRoute>
    },
    {
        path: "/performance_alerts",
        element: <ProtectedRoute>{withSuspense(<PerformanceAlerts />)}</ProtectedRoute>
    },
    {
        path: "/notification_settings",
        element: <ProtectedRoute>{withSuspense(<NotificationSettings />)}</ProtectedRoute>
    },
    {
        path: "*",
        element: withSuspense(<NotFound />)
    }
]);

export default router;
